import { SnackQueue as Queue } from "@/plugins/SnackQueue";

import {
  requestTokenElectronicRequest,
  deleteDoeObrigatorySignatureRequest,
  getRequiredSignatureFiles,
  getRequestedSignatureFiles,
  getSignedSignatureFiles,
  requestSignatureTokens,
  getExternalSignatureToken,
  getExternalSignatureData,
  postExternalSignatureResponse,
  refuseExternalSignature,
  refuseSignatureFile,
  signatureTokensRequest,
  checkTokenElectronicRequest,
  startElectronicSignatureRequest,
  cancelSignatureFile,
  removeSignatureFile,
  createDoeObrigatorySignature,
  authenticateFile,
  checkSignatureProcessFileRequest
} from "@/services/api/process/ApiSignature";
import { General } from "@/utils/constants";
import {
  ResponseGenericType,
  ResponsePaged,
  ResponseType,
  FilterSignaturePaged
} from "@/store/types/types";
import { ActionContext } from "vuex/types";
import {
  IRefuseSignatureId,
  IRefuseSignatureHash,
  IExternalSignatureParmas,
  IExternalSignature,
  ISignatureObject,
  IRequiredSignature,
  ISignatureTask,
  IObrigatorySignature,
  ISimpleFile,
  ITokenCapacity,
  ISignatureId,
  IElectronicSignature,
  ITokenSignature,
  ISignatureCheckFlow,
  ISignatureRequested
} from "@/store/types/signature-type";
import {
  IDocument,
  IProcessFileComplete,
  IProcessFileId,
  ISelectedDocument,
  IValidatedFile
} from "@/store/types/file-type";
import { showErrorMessage, showSuccessMessage } from "@/utils/messageUtility";
import { RootState } from "@/store/types/root";

const state = {
  currentProcessFileId: null as string | null,
  currentProcessFileList: [] as IProcessFileComplete[],
  documentSign: [] as IRequiredSignature[],
  documentSignCount: 0,
  documentRequestedSign: [] as ISignatureRequested[],
  documentRequestedSignCount: 0,
  documentSignedSign: [] as IRequiredSignature[],
  documentSignedSignCount: 0,
  selectedCertificate: "" as string,
  listSelectedDocuments: [] as IDocument[],
  signatureCapacity: null as number | null,
  tokensSignDocuments: [] as string[],
  tokensExternalSignDocuments: {},
  externalSignatureDetails: {} as IExternalSignature,
  listSignatureDocuments: [] as ISignatureObject[]
};

type State = typeof state;

const mutations = {
  documentSignSuccess: (
    state: State,
    data: ResponsePaged<IRequiredSignature>
  ) => {
    state.documentSign = data.results;
    state.documentSignCount = data.count;
  },

  documentRequestedSignSuccess: (
    state: State,
    data: ResponsePaged<ISignatureRequested>
  ) => {
    state.documentRequestedSign = data.results;
    state.documentRequestedSignCount = data.count;
  },

  documentSignedSuccess: (
    state: State,
    data: ResponsePaged<IRequiredSignature>
  ) => {
    state.documentSignedSign = data.results;
    state.documentSignedSignCount = data.count;
  },

  setTokensSignDocuments: (state: State, data: string[]) => {
    state.tokensSignDocuments = data;
  },

  setTokensExternalSignDocuments: (state: State, data: ITokenSignature) => {
    state.tokensExternalSignDocuments = data;
  },

  addExternalSignatureDetails: (state: State, details: IExternalSignature) => {
    state.externalSignatureDetails = details;
  },

  removeExternalUrl: (state: State) => {
    state.externalSignatureDetails = {
      ...state.externalSignatureDetails,
      urlFile: "",
      urlFileToView: ""
    };
  },

  setSelectedCertificate: (state: State, cert: string) => {
    state.selectedCertificate = cert;
  },

  setListSelectedDocuments: (state: State, docs: ISelectedDocument) => {
    state.listSelectedDocuments = docs.documents;
    state.signatureCapacity = docs.capacity_id;
  },

  setCurrentProcessFileId: (state: State, id: string) => {
    state.currentProcessFileId = id;
  },

  setCurrentProcessFileList: (state: State, list: IProcessFileComplete[]) => {
    state.currentProcessFileList = list;
  },

  setListSignatureDocumentsRemovingDuplicates: (
    state: State,
    list: ISignatureObject
  ) => {
    if (Array.isArray(list)) {
      list.forEach(docSignatureToAdd => {
        const index = state.listSignatureDocuments.findIndex(
          signatureDocument =>
            signatureDocument.process_file_id ===
            docSignatureToAdd.process_file_id
        );
        if (index !== -1) {
          state.listSignatureDocuments[index] = docSignatureToAdd;
          state.listSignatureDocuments = [...state.listSignatureDocuments];
        } else state.listSignatureDocuments.push(docSignatureToAdd);
      });
    }
  },

  clearListSignatureDocuments: (state: State) => {
    state.listSignatureDocuments = [];
  },

  checkDoneSignatureDocuments: (state: State) => {
    const hasPendingSignature = state.listSignatureDocuments.some(
      (document: ISignatureObject) =>
        ["pending", "signing"].includes(document.status || "")
    );
    if (!hasPendingSignature) {
      Queue.open({
        id: "sign_documents",
        canClose: true
      });
    }
  },

  setSignatureCapacity: (state: State, capacityId: number) => {
    state.signatureCapacity = capacityId;
  },

  updateStateDocumentSign: (
    state: State,
    { process_file_id, status = "" }: ISignatureTask
  ) => {
    for (const doc of state.listSignatureDocuments) {
      if (
        doc.process_file_id === process_file_id &&
        (doc.signatureType === "ELECTRONIC" ||
          doc.signatureType === "CERTIFICATE")
      ) {
        status && (doc.status = status);
        break;
      }
    }
  }
};

const actions = {
  documentSignRequest: async (
    { commit, getters }: ActionContext<State, RootState>,
    {
      page,
      order_by,
      ordenation,
      paginator_count,
      search,
      filters,
      ignore_order_by_default,
      only_count
    }: FilterSignaturePaged
  ) => {
    commit("documentSignSuccess", { count: 0, results: [] });
    const results: ResponseType<ResponsePaged<
      IRequiredSignature
    >> = await getRequiredSignatureFiles({
      page,
      order_by,
      ordenation,
      paginator_count,
      search,
      filters,
      ignore_order_by_default,
      only_count
    });

    if (results.kind === "ok") {
      const list = (results.data?.results ?? []).map(signature => {
        signature.capacity_request = getters.getCapacityById(
          signature.capacity_request
        );
        signature.process_file.document_type = getters.getFileTypeById(
          signature.process_file.document_type
        );
        signature.process_file.process.priority = getters.getPriorityById(
          signature.process_file.process.priority
        );
        return signature;
      });
      commit("documentSignSuccess", { ...results.data, results: list });
    } else showErrorMessage(results.message);
  },
  documentSignRequested: async (
    { commit, getters }: ActionContext<State, RootState>,
    {
      page,
      order_by,
      ordenation,
      paginator_count,
      search,
      filters,
      ignore_order_by_default,
      only_count
    }: FilterSignaturePaged
  ) => {
    commit("documentRequestedSignSuccess", { count: 0, results: [] });
    const results: ResponseType<ResponsePaged<
      ISignatureRequested
    >> = await getRequestedSignatureFiles({
      page,
      order_by,
      ordenation,
      paginator_count,
      search,
      filters,
      ignore_order_by_default,
      only_count
    });

    if (results.kind === "ok") {
      const list = (results.data?.results ?? []).map(signature => {
        signature.capacity = getters.getCapacityById(signature.capacity);
        signature.process_file.document_type = getters.getFileTypeById(
          signature.process_file.document_type
        );
        signature.process_file.process.priority = getters.getPriorityById(
          signature.process_file.process.priority
        );
        return signature;
      });
      commit("documentRequestedSignSuccess", {
        ...results.data,
        results: list
      });
    } else showErrorMessage(results.message);
  },
  documentSignedRequest: async (
    { commit, getters }: ActionContext<State, RootState>,
    {
      page,
      order_by,
      ordenation,
      paginator_count,
      search,
      filters,
      ignore_order_by_default,
      only_count
    }: FilterSignaturePaged
  ) => {
    commit("documentSignedSuccess", { count: 0, results: [] });
    const results: ResponseType<ResponsePaged<
      IRequiredSignature
    >> = await getSignedSignatureFiles({
      page,
      order_by,
      ordenation,
      paginator_count,
      search,
      filters,
      ignore_order_by_default,
      only_count
    });

    if (results.kind === "ok") {
      const list = (results.data?.results ?? []).map(signature => {
        signature.capacity_request = getters.getCapacityById(
          signature.capacity_request
        );
        signature.process_file.document_type = getters.getFileTypeById(
          signature.process_file.document_type
        );
        signature.process_file.process.priority = getters.getPriorityById(
          signature.process_file.process.priority
        );
        return signature;
      });
      commit("documentSignedSuccess", {
        ...results.data,
        results: list
      });
    } else showErrorMessage(results.message);
  },

  tokensRequest: async (
    { commit }: ActionContext<State, RootState>,
    fileIds: IProcessFileId[]
  ) => {
    const results: ResponseType<string[]> = await requestSignatureTokens(
      fileIds
    );

    if (results.kind === "ok") commit("setTokensSignDocuments", results.data);
    else showErrorMessage(results.message);

    return results.kind === "ok" ? results.data?.[0] : {};
  },

  externalTokensRequest: async (
    { commit }: ActionContext<State, RootState>,
    hash: string
  ) => {
    const results: ResponseType<ITokenSignature> = await getExternalSignatureToken(
      hash
    );

    if (results.kind === "ok")
      commit("setTokensExternalSignDocuments", results.data);
    else showErrorMessage(results.message);
  },

  tokensElectronicResponse: async (
    _: ActionContext<State, RootState>,
    { cpf, password, processFileId, capacity, send_by }: IElectronicSignature
  ) => {
    const results: ResponseGenericType = await requestTokenElectronicRequest({
      cpf,
      password,
      processFileId,
      capacity,
      send_by
    });

    if (results.kind === "ok") {
      return results.data;
    } else showErrorMessage(results.message);
  },

  startElectronicSignature: async (
    _: ActionContext<State, RootState>,
    signatureItem: ISignatureObject
  ) => {
    const results: ResponseType<ITokenSignature> = await startElectronicSignatureRequest(
      signatureItem
    );
    if (results.kind !== "ok") showErrorMessage(results.message);
    return results.kind === "ok";
  },

  electronicSignTrigger: async ({
    state,
    dispatch
  }: ActionContext<State, RootState>) => {
    const hasSigningFile = state.listSignatureDocuments.some(
      (document: ISignatureObject) =>
        document.status === "signing" && document.signatureType === "ELECTRONIC"
    );

    if (!hasSigningFile) {
      const documentToSign = state.listSignatureDocuments.find(
        (document: ISignatureObject) =>
          document.status === "pending" &&
          document.signatureType === "ELECTRONIC"
      );
      if (documentToSign) {
        await dispatch("signingDocument", documentToSign);
      }
    }
  },

  signingDocument: async (
    { commit, dispatch }: ActionContext<State, RootState>,
    documentToSign: ISignatureObject
  ) => {
    commit("updateStateDocumentSign", { ...documentToSign, status: "signing" });
    const hasStarted = await dispatch(
      "startElectronicSignature",
      documentToSign
    );

    if (!hasStarted) {
      commit("updateStateDocumentSign", { ...documentToSign, status: "error" });
      commit("checkDoneSignatureDocuments");
      dispatch("electronicSignTrigger");
      return;
    }

    await dispatch("checkDocumentSignatureStatus", {
      documentToSign,
      triggerAction: "electronicSignTrigger",
      attempts: process.env.VUE_APP_ENVIRONMENT === "development" ? 10 : 3,
      totalWaitTime:
        process.env.VUE_APP_ENVIRONMENT === "development" ? 2e4 : 9e4
    });
  },

  tokensResponse: async (
    { commit, dispatch }: ActionContext<State, RootState>,
    documentToSign: ISignatureTask
  ) => {
    commit("updateStateDocumentSign", { ...documentToSign, status: "signing" });
    const hasStarted = await dispatch(
      "startCertificateSignature",
      documentToSign
    );

    if (!hasStarted) {
      commit("updateStateDocumentSign", { ...documentToSign, status: "error" });
      commit("checkDoneSignatureDocuments");
      dispatch("certificateSignTrigger");
      return;
    }

    await dispatch("checkDocumentSignatureStatus", {
      documentToSign,
      triggerAction: "certificateSignTrigger",
      attempts: process.env.VUE_APP_ENVIRONMENT === "development" ? 10 : 3,
      totalWaitTime:
        process.env.VUE_APP_ENVIRONMENT === "development" ? 2e4 : 9e4
    });
  },

  checkDocumentSignatureStatus: async (
    { commit, dispatch }: ActionContext<State, RootState>,
    {
      documentToSign,
      attempts,
      totalWaitTime,
      triggerAction,
      counter = 1
    }: ISignatureCheckFlow
  ) => {
    if (["done", "error"].includes(documentToSign.status)) return;

    const results = await checkSignatureProcessFileRequest(
      documentToSign.process_file_id
    );

    if (results.data?.signed) {
      commit("updateStateDocumentSign", {
        ...documentToSign,
        status: "done"
      });
      if (documentToSign.toUpdate) {
        if (documentToSign.callback) documentToSign.callback();
        else
          dispatch("processFilesFilteredRequest", {
            files_id: [documentToSign.process_file_id],
            processId: documentToSign.process_id,
            dispatch_id: documentToSign.dispatch_id,
            isArchived: documentToSign.isArchived
          });
      }
      commit("checkDoneSignatureDocuments");
      dispatch(triggerAction);
    } else if (counter < attempts) {
      setTimeout(() => {
        dispatch("checkDocumentSignatureStatus", {
          documentToSign,
          attempts,
          totalWaitTime,
          triggerAction,
          counter: counter + 1
        });
      }, totalWaitTime / attempts);
    } else {
      commit("updateStateDocumentSign", {
        ...documentToSign,
        status: "error"
      });
      commit("checkDoneSignatureDocuments");
      dispatch(triggerAction);
    }
  },

  updateStateDocumentSignWebSocket: (
    { commit, state }: ActionContext<State, RootState>,
    { process_file_id, status = "" }: ISignatureTask
  ) => {
    for (const doc of state.listSignatureDocuments) {
      if (
        doc.process_file_id === process_file_id &&
        (doc.signatureType === "ELECTRONIC" || "CERTIFICATE")
      ) {
        commit("updateStateDocumentSign", {
          process_file_id,
          status
        });
        commit("socketSetDocumentToUpdate", {
          files_id: [doc.process_file_id],
          processId: doc.process_id,
          dispatch_id: doc.dispatch_id,
          isArchived: doc.isArchived,
          isRevertNoEffect: doc.isRevertNoEffect
        });
        break;
      }
    }
  },

  cancelSignatureFileRequest: async (
    _: ActionContext<State, RootState>,
    list_signature: ISignatureId[]
  ) => {
    const results: ResponseGenericType = await cancelSignatureFile(
      list_signature
    );

    if (results.kind !== "ok") showErrorMessage(results.message);
    else
      showSuccessMessage({
        message: General.TEXTS.CANCELED_SUBSCRIPTION_REQUEST
      });
  },

  checkTokenElectronic: async (
    _: ActionContext<State, RootState>,
    { token, capacity }: ITokenCapacity
  ) => {
    const results: ResponseGenericType = await checkTokenElectronicRequest({
      token,
      capacity
    });
    return results.data;
  },

  authenticateFileRequest: async (
    _: ActionContext<State, RootState>,
    list: IValidatedFile[]
  ) => {
    const results: ResponseGenericType = await authenticateFile(list);

    if (results.kind !== "ok") showErrorMessage(results.message);
  },

  deleteSignatureFile: async (
    _: ActionContext<State, RootState>,
    { processFileId, processId }: ISimpleFile
  ) => {
    const results: ResponseGenericType = await removeSignatureFile({
      processFileId,
      processId
    });

    if (results.kind !== "ok") showErrorMessage(results.message);
  },

  createObrigatorySignature: async (
    _: ActionContext<State, RootState>,
    signatureList: IObrigatorySignature[]
  ) => {
    const results: ResponseGenericType = await createDoeObrigatorySignature(
      signatureList
    );

    const result = results.kind === "ok";
    if (!result) showErrorMessage(results.message);
    return result;
  },

  deleteObrigatorySignature: async (
    _: ActionContext<State, RootState>,
    signatureId: string
  ) => {
    const results: ResponseGenericType = await deleteDoeObrigatorySignatureRequest(
      signatureId
    );

    const result = results.kind === "ok";
    if (!result) showErrorMessage(results.message);
    return result;
  },

  certificateSignTrigger: async (
    { state, dispatch }: ActionContext<State, RootState>,
    callback = async () => null
  ) => {
    const hasSigningFile = state.listSignatureDocuments.some(
      (document: ISignatureObject) =>
        document.status === "signing" &&
        document.signatureType === "CERTIFICATE"
    );

    if (!hasSigningFile) {
      const documentToSign = state.listSignatureDocuments.find(
        (document: ISignatureObject) =>
          document.status === "pending" &&
          document.signatureType === "CERTIFICATE"
      );
      documentToSign && (await dispatch("tokensResponse", documentToSign));
      await callback();
    }
  },

  startCertificateSignature: async (
    _: ActionContext<State, RootState>,
    signatureItem: ISignatureObject
  ) => {
    const results: ResponseGenericType = await signatureTokensRequest(
      signatureItem
    );
    if (results.kind !== "ok") showErrorMessage(results.message);
    return results.kind === "ok";
  },

  validateExternalSignatureRequest: async (
    { commit }: ActionContext<State, RootState>,
    hash: string
  ) => {
    const results: ResponseType<IExternalSignature> = await getExternalSignatureData(
        hash
      ),
      status = results.kind === "ok";

    if (status) commit("addExternalSignatureDetails", results.data);
    else commit("postSubscriberError", results.message);
    return status;
  },

  postExternalSignatureResponseAction: async (
    { commit }: ActionContext<State, RootState>,
    { hash, refuse_options }: IExternalSignatureParmas
  ) => {
    const results: ResponseGenericType = await postExternalSignatureResponse(
      hash,
      refuse_options
    );

    if (results.kind === "ok") {
      if (results.data?.message)
        showSuccessMessage({ message: results.data?.message });
    } else commit("postSubscriberError", results.message);
  },

  refuseExternalSignature: async (
    { commit }: ActionContext<State, RootState>,
    { hash, refuse_options }: IRefuseSignatureHash
  ) => {
    const results: ResponseGenericType = await refuseExternalSignature(
      hash,
      refuse_options
    );

    if (results.kind === "ok") {
      showSuccessMessage({ message: General.TEXTS.REQUEST_SIGNATURE_SUCCESS });
    } else commit("postSubscriberError", results.message);
  },

  refuseSignatureFile: async (
    { commit }: ActionContext<State, RootState>,
    signatureList: IRefuseSignatureId[]
  ) => {
    const results: ResponseGenericType = await refuseSignatureFile(
      signatureList
    );

    if (results.kind === "ok") {
      showSuccessMessage({ message: General.TEXTS.REQUEST_SIGNATURE_SUCCESS });
    } else commit("postSubscriberError", results.message);
  }
};

const getters = {
  getCurrentProcessFileId: (state: State) => state.currentProcessFileId,
  getCurrentProcessFileList: (state: State) => state.currentProcessFileList,
  getDocumentSignList: (state: State) => ({
    list: state.documentSign,
    count: state.documentSignCount
  }),
  getDocumentRequestedSignList: (state: State) => ({
    list: state.documentRequestedSign,
    count: state.documentRequestedSignCount
  }),
  getDocumentSignedSignList: (state: State) => ({
    list: state.documentSignedSign,
    count: state.documentSignedSignCount
  }),
  getListSelectedDocuments: (state: State) => state.listSelectedDocuments,
  getSignatureCapacity: (state: State) => state.signatureCapacity,
  getSelectedCertificate: (state: State) => state.selectedCertificate,
  getTokensSignDocuments: (state: State) => state.tokensSignDocuments,
  getTokensExternalSignDocuments: (state: State) =>
    state.tokensExternalSignDocuments,
  getExternalSignatureDetails: (state: State) => state.externalSignatureDetails,
  getListSignatureDocuments: (state: State) => state.listSignatureDocuments
};

export default {
  state,
  mutations,
  actions,
  getters
};
